import {
  ActionType,
  type ActionTypeValue,
  initialActionState,
  type MessageActionState,
} from '@openx/components-deal/lib/DealDetails/constants.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback, useReducer, useState } from 'react';
import { useMessageFeedback } from './useMessageFeedback.ts';

export const messageActionsReducer = (
  state: Record<string, MessageActionState>,
  action: { type: ActionTypeValue; messageId: string }
) => {
  const current = state[action.messageId];

  if (current?.feedbackSubmitted && action.type !== ActionType.THUMB_DOWN) return state;

  switch (action.type) {
    case ActionType.THUMB_UP:
      return {
        ...state,
        [action.messageId]: current?.thumbUp
          ? initialActionState
          : { thumbUp: true, thumbDown: false, requestFeedback: false, feedbackSubmitted: false },
      };
    case ActionType.THUMB_DOWN:
      return {
        ...state,
        [action.messageId]: current?.thumbDown
          ? initialActionState
          : { thumbUp: false, thumbDown: true, requestFeedback: true, feedbackSubmitted: false },
      };
    case ActionType.SUBMIT_FEEDBACK:
      return {
        ...state,
        [action.messageId]: { ...(current ?? initialActionState), requestFeedback: false, feedbackSubmitted: true },
      };
  }
};

export function useMessageFeedbackActions() {
  const { submitFeedback, resetError } = useMessageFeedback();
  const [actionsState, actionsDispatch] = useReducer(messageActionsReducer, {});
  const [submittingMessageId, setSubmittingMessageId] = useState<string | null>(null);

  const submitThumbUpFeedback = useCallback(
    async (langfuseTraceId: string) => {
      await submitFeedback({
        langfuseTraceId,
        score: true,
      });
    },
    [submitFeedback]
  );

  const submitThumbDownFeedback = useCallback(
    async (messageId: string, langfuseTraceId: string, feedbackText: string) => {
      setSubmittingMessageId(messageId);
      try {
        await submitFeedback({
          langfuseTraceId,
          score: false,
          comment: feedbackText,
        });
      } catch (err) {
        resetError();
        throw err;
      } finally {
        setSubmittingMessageId(null);
      }
    },
    [submitFeedback, resetError]
  );

  const handleThumbUp = useCallback(
    async (traceId: string, messageId: string) => {
      const msgState = actionsState[messageId];
      if (msgState?.feedbackSubmitted) return;
      resetError();
      if (!msgState?.thumbUp) {
        try {
          await submitThumbUpFeedback(traceId);
          actionsDispatch({ type: ActionType.THUMB_UP, messageId });
        } catch {
          resetError();
        }
      } else {
        actionsDispatch({ type: ActionType.THUMB_UP, messageId });
      }
    },
    [actionsState, resetError, submitThumbUpFeedback]
  );

  const handleThumbDown = useCallback(
    (messageId: string) => {
      resetError();
      actionsDispatch({ type: ActionType.THUMB_DOWN, messageId });
    },
    [resetError]
  );

  const handleSubmitFeedback = useCallback(
    async (messageId: string, traceId: string, feedbackText: string) => {
      try {
        await submitThumbDownFeedback(messageId, traceId, feedbackText);
        actionsDispatch({ type: ActionType.SUBMIT_FEEDBACK, messageId });
      } catch {
        noop();
      }
    },
    [submitThumbDownFeedback]
  );

  const handleCancelFeedback = useCallback(
    (messageId: string) => {
      resetError();
      actionsDispatch({ type: ActionType.THUMB_DOWN, messageId });
    },
    [resetError]
  );

  return {
    actionsState,
    submittingMessageId,
    handleThumbUp,
    handleThumbDown,
    handleSubmitFeedback,
    handleCancelFeedback,
  };
}
